import { AlertBanner } from '@apollo/orbit';
import { GraphQLSchema } from 'graphql';
import * as monacoEditor from 'monaco-editor';
import React, { useRef } from 'react';

import { useQueryPlan } from 'src/app/graph/explorerPage/helpers/federationHelpers';
import { containsConnectorFetch } from 'src/app/graph/explorerPage/helpers/queryPlanHelpers';
import { useActiveDefinitionDetails } from 'src/app/graph/explorerPage/hooks/useActiveDefinitionDetails';
import { useGraphRef } from 'src/app/graph/hooks/useGraphRef';
import { Loading } from 'src/components/common/loading/Loading';
import { ReadonlyEditor } from 'src/components/editor/ReadonlyEditor';
import { SubgraphAndMetadata } from 'src/lib/graphql/subgraphMetadataHelpers';

import { ConnectorsQueryPlanAlert } from '../connectorsQueryPlanAlert/ConnectorsQueryPlanAlert';

import { Mermaid } from './mermaid/Mermaid';

export const useQueryPlanFromEditor = ({
  operationEditor,
  schema,
  hasSupergraphSchema,
}: {
  operationEditor: monacoEditor.editor.IStandaloneCodeEditor;
  schema: GraphQLSchema;
  hasSupergraphSchema: boolean;
}) => {
  const activeDefinitionDetails = useActiveDefinitionDetails(
    operationEditor,
    schema,
  );
  const activeDocument = activeDefinitionDetails?.activePartsOfDocument;

  const graphRef = useGraphRef();
  const currentResult = useQueryPlan({
    graphRef,
    document: activeDocument ?? '',
    hasSupergraphSchema,
  });

  return currentResult;
};

interface QueryPlanProps extends Omit<QueryPlanRendererProps, 'result'> {
  currentResult: ReturnType<typeof useQueryPlanFromEditor>;
}

export const QueryPlan = ({
  currentResult,
  showMermaidQueryPlan,
  mermaidData: currentMermaidData,
  subgraphMap,
  isPreview,
}: QueryPlanProps) => {
  // We store this in a ref so that when data is loading we don't show a loading
  // screen, instead we show the previous query plan until the new data comes back
  const lastResultRef = useRef({ currentResult, currentMermaidData });
  if (currentResult.state !== 'loading') {
    lastResultRef.current = { currentResult, currentMermaidData };
  }
  const { currentResult: result, currentMermaidData: mermaidData } =
    lastResultRef.current;
  if (result.state === 'loading') {
    return <Loading />;
  }

  if (result.state === 'failed') {
    return (
      <AlertBanner status="warning" className="mt-5">
        Please select a valid operation in your document to generate a query
        plan.
      </AlertBanner>
    );
  }

  return (
    <QueryPlanRenderer
      mermaidData={mermaidData}
      showMermaidQueryPlan={showMermaidQueryPlan}
      result={result}
      subgraphMap={subgraphMap}
      isPreview={isPreview}
    />
  );
};

interface QueryPlanRendererProps {
  mermaidData: string | undefined;
  showMermaidQueryPlan: boolean;
  result: Omit<
    Exclude<ReturnType<typeof useQueryPlan>, { state: 'failed' | 'loading' }>,
    'state'
  >;
  subgraphMap?: Record<string, SubgraphAndMetadata>;
  isPreview?: boolean;
}

export const QueryPlanRenderer = ({
  mermaidData,
  showMermaidQueryPlan,
  result,
  subgraphMap,
  isPreview = false,
}: QueryPlanRendererProps) => {
  const hasConnector =
    subgraphMap && containsConnectorFetch(result.object.node, subgraphMap);

  return (
    <>
      {hasConnector && isPreview ? (
        <ConnectorsQueryPlanAlert className="my-5" />
      ) : null}
      {showMermaidQueryPlan ? (
        <Mermaid mermaidSVGText={mermaidData} />
      ) : (
        <ReadonlyEditor language="queryPlan" src={result.text} />
      )}
    </>
  );
};
